import React from 'react';
import {
    findRevenueDynamicsProduct,
    type RevenueDynamicsProduct,
} from './revenueDynamicsMeta';

/**
 * Drives the chart's product filter: open/close state, the resolved selected
 * product, the products grouped into `Command.Group` sections (insertion
 * order preserved so the fixture controls section order), and the select /
 * clear handlers. Render lives in `RevenueDynamicsProductPickerView`.
 */
export const useRevenueDynamicsProductPicker = (
    productId: string | null,
    setProductId: (id: string | null) => void,
    products: ReadonlyArray<RevenueDynamicsProduct>,
) => {
    const [isOpen, setIsOpen] = React.useState(false);

    const selected = React.useMemo(
        () => findRevenueDynamicsProduct(productId),
        [productId],
    );

    // Group products by category for the Command.Group sections. We
    // walk the products array once and emit groups in insertion order
    // so the fixture controls section ordering.
    const grouped = React.useMemo(() => {
        const buckets = new Map<string, RevenueDynamicsProduct[]>();
        for (const product of products) {
            const list = buckets.get(product.category) ?? [];
            list.push(product);
            buckets.set(product.category, list);
        }
        return Array.from(buckets.entries());
    }, [products]);

    const onSelect = React.useCallback(
        (next: string | null) => {
            setProductId(next);
            setIsOpen(false);
        },
        [setProductId],
    );

    const onClear = React.useCallback(() => setProductId(null), [setProductId]);

    return {
        isOpen,
        onOpenChange: setIsOpen,
        selected,
        grouped,
        onSelect,
        onClear,
    };
};
